/**
 * Transfer Plan Table Component
 * 
 * Displays matched payments in a table format with batch information.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import type { TransferPlanResult, MatchedPayment } from "@/services/transferPlanService";
import { cn } from "@/lib/utils";

interface TransferPlanTableProps {
  result: TransferPlanResult | null;
  loading?: boolean;
}

export function TransferPlanTable({ result, loading }: TransferPlanTableProps) {
  const [searchQuery, setSearchQuery] = React.useState("");

  if (!result) {
    return null;
  }

  // Use the single batch from result
  const batch = React.useMemo(() => {
    if (!result.batch) {
      return null;
    }

    // Filter payments within batch based on search query
    if (!searchQuery.trim()) {
      return result.batch;
    }

    const query = searchQuery.toLowerCase();
    return {
      ...result.batch,
      payments: result.batch.payments.filter((payment) => {
        const order = payment.csvRow.order.toLowerCase();
        const email = payment.csvRow.customerEmail.toLowerCase();
        const callsign = payment.paymentRequest.user?.callsign?.toLowerCase() || "";
        const batchName = payment.batchName?.toLowerCase() || "";

        return (
          order.includes(query) ||
          email.includes(query) ||
          callsign.includes(query) ||
          batchName.includes(query)
        );
      }),
    };
  }, [result.batch, searchQuery]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleString("id-ID", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const calculateOrpusIncome = (amount: number): number => {
    // Formula: =IF(E2=134000,24000,IF(E2=154000,20000,IF(E2=222000,48000,IF(E2=510000,120000,IF(E2=570000,140000,140000)))))
    if (amount === 134000) {
      return 24000;
    } else if (amount === 154000) {
      return 20000;
    } else if (amount === 222000) {
      return 48000;
    } else if (amount === 510000) {
      return 120000;
    } else if (amount === 570000) {
      return 140000;
    } else {
      return 140000; // Default value
    }
  };

  const calculateOrdaIncome = (amount: number): number => {
    // Formula: =IF(H2=134000,40000,IF(H2=154000,40000,IF(H2=222000,72000,IF(H2=510000,180000,IF(H2=570000,200000,200000)))))
    // H2 is the Amount column (same as E2, just different column reference in Excel)
    if (amount === 134000) {
      return 40000;
    } else if (amount === 154000) {
      return 40000;
    } else if (amount === 222000) {
      return 72000;
    } else if (amount === 510000) {
      return 180000;
    } else if (amount === 570000) {
      return 200000;
    } else {
      return 200000; // Default value
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Transfer Plan Results</CardTitle>
            <CardDescription>
              {result.summary.matchedCount} matched payments in 1 batch
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by order, email, callsign..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-9"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : !batch ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">No batch data available</p>
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch</TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Settlement Time</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Transaction Fee</TableHead>
                  <TableHead>WMAX INCOME</TableHead>
                  <TableHead>ORPUS INCOME</TableHead>
                  <TableHead>ORDA INCOME</TableHead>
                  <TableHead>ORLOK INCOME</TableHead>
                  <TableHead>Province</TableHead>
                  <TableHead>Kabkot</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batch.payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                      No payments found
                    </TableCell>
                  </TableRow>
                ) : (
                  batch.payments.map((payment, index) => (
                    <TableRow key={`${payment.csvRow.order}-${index}`}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {payment.batchName || "UNBATCHED"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {payment.csvRow.order.split("-")[0]}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(payment.csvRow.settlementTime)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(payment.csvRow.amount)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(payment.csvRow.transactionFee)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(30000 - payment.csvRow.transactionFee)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(calculateOrpusIncome(payment.csvRow.amount))}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(calculateOrdaIncome(payment.csvRow.amount))}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(calculateOrdaIncome(payment.csvRow.amount))}
                      </TableCell>
                      <TableCell className="text-sm">
                        {payment.paymentRequest.province || "-"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {payment.paymentRequest.kabkot || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            payment.paymentRequest.status === "Settlement"
                              ? "default"
                              : "secondary"
                          }
                        >
                          {payment.paymentRequest.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

